import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../api/client';

interface EmployeeDetail {
  id: string;
  name: string;
  email: string;
  assigned_cubicle: string | null;
  role: string;
  created_at: string;
  current_session: {
    id: string;
    start_time: string;
    sedentary_duration_seconds: number;
    avg_posture_score: number | null;
    movement_events: number;
  } | null;
  today_stats: {
    sessions_count: number;
    total_sedentary_minutes: number;
    avg_posture_score: number | null;
    alerts_count: number;
  };
  weekly_stats: {
    sessions_count: number;
    total_sedentary_minutes: number;
    avg_posture_score: number | null;
    alerts_count: number;
  };
}

interface Alert {
  id: string;
  alert_type: string;
  message: string;
  timestamp: string;
  delivered: boolean;
}

export function AdminEmployeeDetail() {
  const { employeeId } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState<EmployeeDetail | null>(null);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'alerts'>('overview');

  useEffect(() => {
    const fetchData = async () => {
      if (!employeeId) return;
      try {
        const [empRes, alertsRes] = await Promise.all([
          apiClient.get(`/api/admin/employees/${employeeId}`),
          apiClient.get(`/api/admin/employees/${employeeId}/alerts`, { params: { page_size: 50 } })
        ]);
        setEmployee(empRes.data);
        setAlerts(alertsRes.data.alerts);
        setError('');
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load employee data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [employeeId]);

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const getPostureColor = (score: number | null): string => {
    if (score === null) return '#9CA3AF';
    if (score >= 70) return '#10B981';
    if (score >= 50) return '#F59E0B';
    return '#EF4444';
  };

  const getAlertConfig = (type: string) => {
    switch (type) {
      case 'break_reminder': return { icon: '⏰', color: '#F59E0B', bg: '#FEF3C7' };
      case 'posture_correction': return { icon: '🧘', color: '#3B82F6', bg: '#DBEAFE' };
      case 'hydration_reminder': return { icon: '💧', color: '#06B6D4', bg: '#CFFAFE' };
      case 'cold_detection': return { icon: '🧣', color: '#8B5CF6', bg: '#EDE9FE' };
      default: return { icon: '📢', color: '#6B7280', bg: '#F3F4F6' };
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner} />
        <p>Loading employee details...</p>
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div style={styles.container}>
        <button onClick={() => navigate('/admin')} style={styles.backBtn}>
          ← Back to Dashboard
        </button>
        <div style={styles.errorBox}>{error || 'Employee not found'}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Back Button */}
      <button onClick={() => navigate('/admin')} style={styles.backBtn}>
        ← Back to Dashboard
      </button>

      {/* Employee Header Card */}
      <div style={styles.headerCard}>
        <div style={styles.headerLeft}>
          <div style={styles.avatar}>
            {employee.name.charAt(0).toUpperCase()}
          </div>
          <div style={styles.headerInfo}>
            <h1 style={styles.employeeName}>{employee.name}</h1>
            <p style={styles.employeeEmail}>{employee.email}</p>
            <div style={styles.headerMeta}>
              {employee.assigned_cubicle && (
                <span style={styles.metaTag}>
                  <span>📍</span> {employee.assigned_cubicle}
                </span>
              )}
              <span style={styles.metaTag}>
                <span>👤</span> {employee.role}
              </span>
            </div>
          </div>
        </div>
        
        {employee.current_session && (
          <div style={styles.liveSession}>
            <div style={styles.liveDot} />
            <div>
              <span style={styles.liveLabel}>Live Session</span>
              <span style={styles.liveTime}>
                Started {new Date(employee.current_session.start_time).toLocaleTimeString()}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {(['overview', 'alerts'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              ...styles.tab,
              ...(activeTab === tab ? styles.tabActive : {}),
            }}
          >
            {tab === 'overview' ? '📊 Overview' : `🔔 Alerts (${alerts.length})`}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div style={styles.content}>
          {/* Current Session */}
          {employee.current_session && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>
                <span>🟢</span> Current Session
              </h2>
              <div style={styles.currentSessionGrid}>
                <div style={styles.sessionMetricCard}>
                  <span style={styles.sessionMetricIcon}>⏱️</span>
                  <div>
                    <span style={styles.sessionMetricValue}>
                      {formatDuration(employee.current_session.sedentary_duration_seconds)}
                    </span>
                    <span style={styles.sessionMetricLabel}>Sedentary Time</span>
                  </div>
                </div>
                <div style={styles.sessionMetricCard}>
                  <span style={styles.sessionMetricIcon}>🧘</span>
                  <div>
                    <span style={{
                      ...styles.sessionMetricValue,
                      color: getPostureColor(employee.current_session.avg_posture_score)
                    }}>
                      {employee.current_session.avg_posture_score?.toFixed(0) ?? '--'}
                    </span>
                    <span style={styles.sessionMetricLabel}>Posture Score</span>
                  </div>
                </div>
                <div style={styles.sessionMetricCard}>
                  <span style={styles.sessionMetricIcon}>💫</span>
                  <div>
                    <span style={styles.sessionMetricValue}>
                      {employee.current_session.movement_events}
                    </span>
                    <span style={styles.sessionMetricLabel}>Movements</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Stats Comparison */}
          <div style={styles.statsComparison}>
            {/* Today */}
            <div style={styles.statsCard}>
              <div style={styles.statsCardHeader}>
                <span style={styles.statsCardIcon}>📅</span>
                <h3 style={styles.statsCardTitle}>Today</h3>
              </div>
              <div style={styles.statsGrid}>
                <div style={styles.statItem}>
                  <span style={styles.statValue}>{employee.today_stats.sessions_count}</span>
                  <span style={styles.statLabel}>Sessions</span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statValue}>{employee.today_stats.total_sedentary_minutes}m</span>
                  <span style={styles.statLabel}>Sedentary</span>
                </div>
                <div style={styles.statItem}>
                  <span style={{...styles.statValue, color: getPostureColor(employee.today_stats.avg_posture_score)}}>
                    {employee.today_stats.avg_posture_score?.toFixed(0) ?? '--'}
                  </span>
                  <span style={styles.statLabel}>Avg Posture</span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statValue}>{employee.today_stats.alerts_count}</span>
                  <span style={styles.statLabel}>Alerts</span>
                </div>
              </div>
            </div>

            {/* This Week */}
            <div style={styles.statsCard}>
              <div style={styles.statsCardHeader}>
                <span style={styles.statsCardIcon}>📊</span>
                <h3 style={styles.statsCardTitle}>This Week</h3>
              </div>
              <div style={styles.statsGrid}>
                <div style={styles.statItem}>
                  <span style={styles.statValue}>{employee.weekly_stats.sessions_count}</span>
                  <span style={styles.statLabel}>Sessions</span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statValue}>{employee.weekly_stats.total_sedentary_minutes}m</span>
                  <span style={styles.statLabel}>Sedentary</span>
                </div>
                <div style={styles.statItem}>
                  <span style={{...styles.statValue, color: getPostureColor(employee.weekly_stats.avg_posture_score)}}>
                    {employee.weekly_stats.avg_posture_score?.toFixed(0) ?? '--'}
                  </span>
                  <span style={styles.statLabel}>Avg Posture</span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statValue}>{employee.weekly_stats.alerts_count}</span>
                  <span style={styles.statLabel}>Alerts</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <div style={styles.content}>
          {alerts.length === 0 ? (
            <div style={styles.emptyState}>
              <span style={styles.emptyIcon}>🎉</span>
              <p>No alerts for this employee</p>
            </div>
          ) : (
            <div style={styles.alertList}>
              {alerts.map((alert) => {
                const config = getAlertConfig(alert.alert_type);
                return (
                  <div key={alert.id} style={styles.alertCard}>
                    <div style={{...styles.alertIcon, background: config.bg}}>
                      <span>{config.icon}</span>
                    </div>
                    <div style={styles.alertContent}>
                      <div style={styles.alertHeader}>
                        <span style={{...styles.alertType, color: config.color}}>
                          {alert.alert_type.replace('_', ' ')}
                        </span>
                        <span style={styles.alertTime}>
                          {new Date(alert.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p style={styles.alertMessage}>{alert.message}</p>
                    </div>
                    {alert.delivered && (
                      <span style={styles.deliveredBadge}>✓ Delivered</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: { maxWidth: '1200px', margin: '0 auto' },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50vh',
    gap: '16px',
    color: '#6B7280',
  },
  loadingSpinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #E5E7EB',
    borderTopColor: '#7C3AED',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  backBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    background: 'transparent',
    border: 'none',
    color: '#7C3AED',
    fontWeight: 600,
    fontSize: '14px',
    cursor: 'pointer',
    marginBottom: '20px',
  },
  errorBox: {
    padding: '20px',
    background: '#FEE2E2',
    borderRadius: '12px',
    color: '#DC2626',
    textAlign: 'center',
  },
  headerCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '28px',
    background: 'linear-gradient(135deg, #1E1B4B 0%, #312E81 100%)',
    borderRadius: '20px',
    marginBottom: '24px',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '20px' },
  avatar: {
    width: '72px',
    height: '72px',
    borderRadius: '18px',
    background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '28px',
    fontWeight: 700,
    boxShadow: '0 8px 24px rgba(124, 58, 237, 0.4)',
  },
  headerInfo: {},
  employeeName: { fontSize: '26px', fontWeight: 700, color: 'white', margin: 0 },
  employeeEmail: { fontSize: '14px', color: 'rgba(255,255,255,0.7)', marginTop: '4px' },
  headerMeta: { display: 'flex', gap: '12px', marginTop: '12px' },
  metaTag: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '8px',
    fontSize: '13px',
    color: 'rgba(255,255,255,0.9)',
  },
  liveSession: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 20px',
    background: 'rgba(16, 185, 129, 0.15)',
    borderRadius: '14px',
    border: '1px solid rgba(16, 185, 129, 0.3)',
  },
  liveDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    background: '#10B981',
    boxShadow: '0 0 12px rgba(16, 185, 129, 0.6)',
    animation: 'pulse 2s ease-in-out infinite',
  },
  liveLabel: { display: 'block', fontSize: '14px', fontWeight: 600, color: '#10B981' },
  liveTime: { display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginTop: '2px' },
  tabs: { display: 'flex', gap: '8px', marginBottom: '24px' },
  tab: {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '12px',
    background: 'white',
    color: '#6B7280',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
  tabActive: {
    background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
    color: 'white',
    boxShadow: '0 4px 15px rgba(124, 58, 237, 0.3)',
  },
  content: {},
  section: { marginBottom: '24px' },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '18px',
    fontWeight: 600,
    color: '#1E1B4B',
    marginBottom: '16px',
  },
  currentSessionGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' },
  sessionMetricCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '20px',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  },
  sessionMetricIcon: { fontSize: '32px' },
  sessionMetricValue: { display: 'block', fontSize: '28px', fontWeight: 700, color: '#1E1B4B' },
  sessionMetricLabel: { display: 'block', fontSize: '13px', color: '#6B7280', marginTop: '2px' },
  statsComparison: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' },
  statsCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  },
  statsCardHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' },
  statsCardIcon: { fontSize: '24px' },
  statsCardTitle: { fontSize: '18px', fontWeight: 600, color: '#1E1B4B', margin: 0 },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' },
  statItem: { textAlign: 'center' },
  statValue: { display: 'block', fontSize: '28px', fontWeight: 700, color: '#1E1B4B' },
  statLabel: { display: 'block', fontSize: '12px', color: '#6B7280', marginTop: '4px' },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '60px',
    background: 'white',
    borderRadius: '20px',
    color: '#6B7280',
  },
  emptyIcon: { fontSize: '48px', marginBottom: '12px' },
  alertList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  alertCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '20px',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  },
  alertIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '22px',
  },
  alertContent: { flex: 1 },
  alertHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: '6px' },
  alertType: { fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' },
  alertTime: { fontSize: '12px', color: '#9CA3AF' },
  alertMessage: { fontSize: '14px', color: '#1F2937', margin: 0 },
  deliveredBadge: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#10B981',
    background: '#D1FAE5',
    padding: '6px 10px',
    borderRadius: '6px',
  },
};
